import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import { useSyncConfig } from 'FrontRoyalConfig/useConfig';
import { useCurrentUser } from 'FrontRoyalAngular';
import { useMatch } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationCrosshairs } from '@fortawesome/pro-solid-svg-icons/faLocationCrosshairs';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { faMinus } from '@fortawesome/pro-solid-svg-icons/faMinus';
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { classifyLocationError, useLocationErrorToast } from '../hooks/useLocationErrorToast';
import { STUDENT_NETWORK_MAP_STYLE } from '../constants/mapStyle';
import { STUDENT_NETWORK_MAP_ID } from '../constants/mapConstants';

const DEFAULT_ZOOM = 3;
const MIN_ZOOM = 3;
const MAX_ZOOM = 10;

function getInitialCenter(userPlaceDetails: { lat?: number; lng?: number } | undefined) {
    const { lat = 30, lng = -100 } = userPlaceDetails || {};
    const width = window.innerWidth;

    const centerLat = width > 1000 ? 23.45 : lat;
    let centerLng = lng;

    if (width > 1400) {
        centerLng = -7.39;
    } else if (width > 1000) {
        centerLng = -42;
    }

    return { lat: centerLat, lng: centerLng };
}

// Bottom offset for map controls: clears the 55px mobile nav bar + safe area inset on mobile, fixed offset on desktop
const CONTROLS_BOTTOM_CLASS = 'bottom-[calc(16px+55px+env(safe-area-inset-bottom))] md:bottom-4';

const controlButtonClass = 'flex h-10 w-10 items-center justify-center bg-white shadow-[0_2px_6px_0_rgba(0,0,0,0.3)]';

function MapControls({ isMapView }: { isMapView: boolean }) {
    const map = useMap(STUDENT_NETWORK_MAP_ID);
    const showLocationErrorToast = useLocationErrorToast();
    const iconColor = isMapView ? 'text-slate-grey-600' : 'text-slate-grey-300';

    const handleZoomIn = useCallback(() => {
        if (!map) return;
        map.setZoom((map.getZoom() ?? DEFAULT_ZOOM) + 1);
    }, [map]);

    const handleZoomOut = useCallback(() => {
        if (!map) return;
        map.setZoom((map.getZoom() ?? DEFAULT_ZOOM) - 1);
    }, [map]);

    const handleMyLocation = useCallback(async () => {
        if (!map) return;
        try {
            let lat: number;
            let lng: number;

            if (Capacitor.isNativePlatform()) {
                const position = await Geolocation.getCurrentPosition();
                lat = position.coords.latitude;
                lng = position.coords.longitude;
            } else {
                if (!navigator.geolocation) return;
                const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject);
                });
                lat = position.coords.latitude;
                lng = position.coords.longitude;
            }

            map.panTo({ lat, lng });
            map.setZoom(8);
        } catch (error) {
            showLocationErrorToast(await classifyLocationError(error));
        }
    }, [map, showLocationErrorToast]);

    return (
        <div className={`absolute end-2.5 z-10 flex flex-col gap-2 ${CONTROLS_BOTTOM_CLASS}`}>
            <button
                type="button"
                onClick={handleMyLocation}
                className={`${controlButtonClass} rounded-sm`}
                aria-label="My location"
            >
                <FontAwesomeIcon icon={faLocationCrosshairs} className={`text-lg ${iconColor}`} />
            </button>
            <div className="flex flex-col overflow-hidden rounded-sm shadow-[0_2px_6px_0_rgba(0,0,0,0.3)]">
                <button
                    type="button"
                    onClick={handleZoomIn}
                    className="flex h-10 w-10 items-center justify-center border-b border-slate-grey-100 bg-white"
                    aria-label="Zoom in"
                >
                    <FontAwesomeIcon icon={faPlus} className={`text-base ${iconColor}`} />
                </button>
                <button
                    type="button"
                    onClick={handleZoomOut}
                    className="flex h-10 w-10 items-center justify-center bg-white"
                    aria-label="Zoom out"
                >
                    <FontAwesomeIcon icon={faMinus} className={`text-base ${iconColor}`} />
                </button>
            </div>
        </div>
    );
}

function MapPanner({ target }: { target: { lat: number; lng: number } | null }) {
    const map = useMap(STUDENT_NETWORK_MAP_ID);

    useEffect(() => {
        if (!map || !target) return;
        const { lat, lng } = target;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

        // Mobile: no panel overlay, just center the marker
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
            map.panTo({ lat, lng });
            return;
        }

        // Desktop: offset so the cluster lands at ~80% of the viewport width,
        // to the right of the nav column + 600px results panel.
        try {
            const projection = map.getProjection();
            if (!projection) {
                map.panTo({ lat, lng });
                return;
            }
            const zoom = map.getZoom() ?? DEFAULT_ZOOM;
            const scale = 2 ** zoom;
            const worldPoint = projection.fromLatLngToPoint(new google.maps.LatLng(lat, lng));
            if (!worldPoint) {
                map.panTo({ lat, lng });
                return;
            }
            const offsetPixels = window.innerWidth * 0.3;
            const offsetWorld = offsetPixels / scale;
            const adjustedPoint = new google.maps.Point(worldPoint.x - offsetWorld, worldPoint.y);
            const adjustedLatLng = projection.fromPointToLatLng(adjustedPoint);
            if (adjustedLatLng) {
                map.panTo(adjustedLatLng);
            } else {
                map.panTo({ lat, lng });
            }
        } catch {
            map.panTo({ lat, lng });
        }
    }, [map, target]);

    return null;
}

function NetworkMap({
    children,
    isMapView,
    panTo,
}: {
    children?: ReactNode;
    isMapView: boolean;
    panTo?: { lat: number; lng: number } | null;
}) {
    const currentUser = useCurrentUser();
    const map = useMap(STUDENT_NETWORK_MAP_ID);

    const initialCenter = useMemo(
        () => getInitialCenter(currentUser?.careerProfile?.placeDetails ?? undefined),
        [currentUser?.careerProfile?.placeDetails],
    );

    // Google Maps won't load tiles when the container is behind other z-index layers.
    // A window resize event forces the map to re-measure and fetch tiles. Trigger it
    // whenever the map instance becomes available (covers initial load on non-map
    // routes) and again when navigating to/from the map view.
    useEffect(() => {
        if (!map) return;
        window.dispatchEvent(new Event('resize'));
    }, [map, isMapView]);

    return (
        <>
            <Map
                id="student-network-map"
                defaultCenter={initialCenter}
                defaultZoom={DEFAULT_ZOOM}
                minZoom={MIN_ZOOM}
                maxZoom={MAX_ZOOM}
                styles={STUDENT_NETWORK_MAP_STYLE}
                backgroundColor="#F2F3F5"
                disableDefaultUI
                gestureHandling="greedy"
                className="h-full w-full"
            >
                {children}
                <MapPanner target={panTo ?? null} />
            </Map>
            <MapControls isMapView={isMapView} />
        </>
    );
}

// Guards against a race condition where the Google Maps JS API is partially loaded
// (window.google exists from another module, but maps.importLibrary isn't ready yet).
// APIProvider would fail if mounted in that half-loaded state. The three cases:
//   1. API fully loaded (importLibrary exists) → ready immediately
//   2. API not started (no window.google) → ready immediately, let APIProvider load it
//   3. API mid-load (window.google exists, no importLibrary) → poll until ready
function useGoogleMapsReady() {
    const apiReady = typeof window.google?.maps?.importLibrary === 'function';
    const apiLoading = !apiReady && typeof window.google !== 'undefined';
    const [ready, setReady] = useState(apiReady || !apiLoading);

    useEffect(() => {
        if (ready) return undefined;
        const id = setInterval(() => {
            if (typeof window.google?.maps?.importLibrary === 'function') {
                setReady(true);
                clearInterval(id);
            }
        }, 100);
        return () => clearInterval(id);
    }, [ready]);

    return ready;
}

type MapBackgroundProps = {
    mapOverlays?: ReactNode;
    panTo?: { lat: number; lng: number } | null;
    blur?: boolean;
};

export function MapBackground({ mapOverlays, panTo, blur }: MapBackgroundProps) {
    const config = useSyncConfig();
    const apiKey = config.google_api_key;
    const isMapView = !!useMatch('/student-network/map');
    const canMount = useGoogleMapsReady();

    if (!apiKey) return <div className="absolute inset-0 bg-slate-grey-100-alt" />;

    return (
        <div
            className={`absolute inset-0 transition-[filter] duration-300 ${blur ? 'blur-sm' : ''} ${
                isMapView ? 'z-20' : 'z-0'
            }`}
        >
            {canMount && (
                <APIProvider apiKey={apiKey}>
                    <NetworkMap isMapView={isMapView} panTo={panTo}>
                        {mapOverlays}
                    </NetworkMap>
                </APIProvider>
            )}
            {/* Semi-transparent overlay to dim the map on non-map views. We avoid setting
                opacity on the map container itself because the browser's compositor may skip
                tile rendering for layers fully covered by higher z-index content. */}
            <div
                className={`pointer-events-none absolute inset-0 bg-white transition-opacity duration-150 ${
                    isMapView ? 'opacity-0' : 'opacity-60'
                }`}
            />
        </div>
    );
}
